<template>
  <div
    :style="styles"
    :class="classes"
  >
    <img
      v-if="is404"
      :src="RightDecorator"
      alt=""
      class="smart-banner__decorator smart-banner__decorator--right"
    >
    <img
      v-if="hasDecorator"
      :src="DefaultDecoratorB"
      alt=""
      class="smart-banner__defaultDecorator smart-banner__defaultDecorator--right"
    >
    <div :class="['smart-banner__content', { reduceWidth: reduceWidth }]">
      <div v-if="hasBreadcrumb">
        <slot name="breadcrumb" />
      </div>
      <img
        v-if="is404"
        :src="Error"
        alt=""
        class="smart-banner__error"
      >
      <c-kicker
        v-if="hasKicker"
        :color="kickerColor"
        :weight="kickerWeight"
        :align="kickerAlign"
        class="smart-banner__kicker"
      >
        <slot name="kicker" />
      </c-kicker>
      <c-title
        :color="titleColor"
        :size="titleSize"
        :weight="titleWeight"
        :align="titleAlign"
      >
        <slot name="title" />
      </c-title>
      <c-text
        :color="textColor"
        :size="textSize"
        :weight="textWeight"
        :align="textAlign"
        class="smart-banner__text"
      >
        <slot name="text" />
      </c-text>
      <div
        v-if="hasButton"
        :class="['smart-banner__buttons', { centerItems: centerItems }]"
        @click="pushGTM"
      >
        <slot name="button" />
      </div>
    </div>
    <div
      v-if="hasImage || hasFixedImage"
      class="smart-banner__image"
    >
      <c-image
        :image-mobile="imageMobile"
        :image-tablet="imageTablet"
        :image-desktop="imageDesktop"
        :alt="alt"
        :class="{ 'smart-banner__fixedImage': hasFixedImage }"
      />
    </div>
    <div
      v-if="hasVideo"
      class="smart-banner__video"
    >
      <slot name="video" />
    </div>
    <img
      v-if="is404"
      :src="LeftDecorator"
      alt=""
      class="smart-banner__decorator smart-banner__decorator--left"
    >
    <img
      v-if="hasDecorator"
      :src="DefaultDecoratorA"
      alt=""
      class="smart-banner__defaultDecorator smart-banner__defaultDecorator--left"
    >
  </div>
</template>

<script>
import {
  computed, useSlots, nextTick, onMounted
} from 'vue'
import CKicker from '@atoms/kicker/kicker.vue'
import CTitle from '@atoms/title/title.vue'
import CText from '@atoms/text/text.vue'
import CImage from '@atoms/image/image.vue'
import Error from '@images/redesign/png/error-404.png'
import LeftDecorator from '@images/redesign/png/error-404-left-decorator.png'
import RightDecorator from '@images/redesign/png/error-404-right-decorator.png'
import DefaultDecoratorA from '@images/redesign/png/sb-decorator-01.png'
import DefaultDecoratorB from '@images/redesign/png/sb-decorator-02.png'
import isOutbound from '@tagging/isOutbound.js'

export default {
  name: 'smart-banner',
  components: {
    CKicker,
    CTitle,
    CText,
    CImage,
  },
  props: {
    bgColor: {
      type: String,
    },
    direction: {
      type: String,
    },
    hasKicker: {
      type: Boolean,
      default: false,
    },
    kickerColor: {
      type: String,
      default: '#717D96',
    },
    kickerWeight: {
      type: String,
      default: '700',
    },
    kickerAlign: {
      type: String,
    },
    titleColor: {
      type: String,
    },
    titleSize: {
      type: String,
      default: 'h2',
    },
    titleWeight: {
      type: String,
      default: '400',
    },
    titleAlign: {
      type: String,
    },
    textColor: {
      type: String,
    },
    textSize: {
      type: String,
      default: 'xs',
    },
    textWeight: {
      type: String,
      default: '400',
    },
    textAlign: {
      type: String,
    },
    hasButton: {
      type: Boolean,
      default: false,
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    imageMobile: {
      type: String,
    },
    imageTablet: {
      type: String,
    },
    imageDesktop: {
      type: String,
    },
    alt: {
      type: String,
    },
    centerItems: {
      type: Boolean,
      default: false,
    },
    reduceWidth: {
      type: Boolean,
      default: false,
    },
    hasVideo: {
      type: Boolean,
      default: false,
    },
    is404: {
      type: Boolean,
      default: false,
    },
    hasDecorator: {
      type: Boolean,
      default: false,
    },
    hasFixedImage: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const classes = computed(() => [
      'smart-banner',
      `smart-banner--${props.direction}`,
      props.is404 ? 'smart-banner--is404' : '',
      props.hasDecorator ? 'smart-banner--defaultDecorator' : '',
      props.hasFixedImage ? 'smart-banner--fixedImage' : '',
    ])

    const styles = computed(() => ({
      backgroundColor: props.bgColor,
    }))

    const hasBreadcrumb = useSlots().breadcrumb && useSlots().breadcrumb()[0].children.length > 0
    const kicker = useSlots().kicker?.()[0]?.children[0]?.children
    const title = useSlots().title?.()[0]?.children[0]?.children

    const pushGTM = (event) => {
      const target = event.target.closest('a')

      if (target) {
        window.dataLayer.push({
          event: 'click',
          section: props.is404 ? 'pagina-erro' : kicker || 'vazio',
          outbound: isOutbound(target),
          name: title || '',
          label: target.innerText || target.getAttribute('aria-label') || ''
        })
      }
    }

    onMounted(() => {
      nextTick(() => {
        const checkError = document.querySelector('.smart-banner--is404')
        if (checkError) {
          window.dataLayer.push({
            event: 'error',
            error_code: 404,
          })
        }
      })
    })

    return {
      styles,
      classes,
      hasBreadcrumb,
      pushGTM,
    }
  },
  data() {
    return {
      Error,
      LeftDecorator,
      RightDecorator,
      DefaultDecoratorA,
      DefaultDecoratorB,
    }
  },
}
</script>

<style lang="scss">
.smart-banner {
  h1 {
    @include h1();
  }

  h2 {
    @include h2();
  }

  h3 {
    @include h3();
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
  padding: 2rem 0;

  @include screen-size("xl") {
    padding: 3rem 0;
  }

  &__text {
    display: block !important;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &.reduceWidth {
      padding: 2rem;

      @include screen-size("md") {
        padding: 2rem 4rem;
      }

      @include screen-size("xl") {
        padding: 2rem 12rem;
      }

      @media screen and (min-width: 1512px) {
        padding: 2rem 17rem;
      }
    }
  }

  &__content,
  &__image,
  &__video {
    flex: 1;
  }

  &__video {
    display: contents;
    overflow: hidden;

    @include screen-size("xl") {
      display: unset;
    }
  }

  &__fixedImage {
    display: none;

    @include screen-size("xl") {
      display: block;
      width: 37.5rem !important;
      position: relative;
      bottom: 22rem;
      left: 12rem;
    }

    @include screen-size("xxl") {
      left: 20rem;
    }
  }

  &--fixedImage {
    align-items: start;
    overflow: hidden;
    padding-inline: 2rem;

    @include screen-size("xl") {
      height: 18rem;
    }

    @include screen-size("xxl") {
      padding-left: 9rem;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-top: 1.5rem;
    gap: 1rem;

    &.centerItems {
      display: flex;
      flex-direction: column;
      justify-content: center;

      @include screen-size("md") {
        flex-direction: row;
      }
    }

    @include screen-size("xs") {
      flex-direction: row;
      align-items: center;
    }
  }

  &__defaultDecorator,
  &__decorator {
    display: none;

    @include screen-size("xl") {
      display: block;
      position: relative;

      &--left {
        right: 6rem;
        bottom: 10rem;
      }

      &--right {
        left: 6rem;
      }
    }
  }

  &__decorator {
    @include screen-size("xl") {
      width: 33%;
      top: 16.5rem;

      &--left {
        bottom: unset;
      }
    }

    @include screen-size("xxl") {
      width: 35%;
    }
  }

  &__error {
    width: 6rem;
    margin: 0 auto;
    padding-bottom: 1rem;
  }

  &--is404 {
    height: 37.5rem;
    @extend .smart-banner--defaultDecorator;
  }

  &--defaultDecorator {
    overflow: hidden;
    gap: unset;
  }

  @include direction();
}
</style>
